import { useCallback, useEffect, useRef, useState } from 'react';

// Fraction of visible width one arrow press travels; slightly under a full
// screen so a card or two stays on-screen as an anchor.
const PAGE_FRACTION = 0.9;

// Pointer travel before a press becomes a drag rather than a card click.
const DRAG_THRESHOLD = 5;

// Scroll behaviour for the match bar: prev/next enablement and paging. The bar
// always opens flush left - the FTL only ever sends rounds from the current one
// onward - so there is no opening scroll position to compute. The scroll listener
// attaches to the DOM node directly rather than via ScrollAreaViewport's `onScroll`,
// so it doesn't depend on how that merges with Base UI's own edge-fade/scrollbar sync.
export const useScrollControls = () => {
    const containerRef = useRef(null);
    const viewportRef = useRef(null);
    const resolvedViewport = useRef(null);

    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(true);

    // Round whose header is currently pinned to the left edge, if any.
    const [stuckRoundNumber, setStuckRoundNumber] = useState(null);

    // Falls back to Base UI's `data-slot` rather than relying on how
    // ScrollAreaViewport forwards our ref through its spread rest props.
    const getViewport = useCallback(() => {
        if (!resolvedViewport.current) {
            resolvedViewport.current =
                viewportRef.current ??
                containerRef.current?.querySelector(
                    '[data-slot="scroll-area-viewport"]'
                ) ??
                null;
        }

        return resolvedViewport.current;
    }, []);

    // Recomputes arrow enablement and which header is currently pinned.
    const sync = useCallback(() => {
        const viewport = getViewport();

        if (!viewport) {
            return;
        }

        const { scrollLeft, scrollWidth, clientWidth } = viewport;
        const maxScroll = scrollWidth - clientWidth;

        setCanScrollPrev(scrollLeft > 1);
        setCanScrollNext(scrollLeft < maxScroll - 1);

        // Only the pinned header needs the scrim that hides the cards passing
        // behind it; mid-rail headers have nothing behind them, so a scrim
        // there just fades their neighbouring card for no reason. Groups are in
        // rail order, so the last one starting left of the edge is the one
        // currently straddling it - and none does when the rail sits flush.
        const edge = viewport.getBoundingClientRect().left;
        const straddling = [...viewport.querySelectorAll('[data-round-number]')]
            .reverse()
            .find((group) => group.getBoundingClientRect().left < edge - 1);

        setStuckRoundNumber(
            straddling ? Number(straddling.dataset.roundNumber) : null
        );
    }, [getViewport]);

    // Moves the rail by just under one screen; returns the target scroll
    // position so callers can describe the destination mid-animation.
    const page = useCallback(
        (direction) => {
            const viewport = getViewport();

            if (!viewport) {
                return null;
            }

            const distance = direction * viewport.clientWidth * PAGE_FRACTION;

            viewport.scrollBy({ left: distance, behavior: 'smooth' });

            return Math.max(
                0,
                Math.min(
                    viewport.scrollLeft + distance,
                    viewport.scrollWidth - viewport.clientWidth
                )
            );
        },
        [getViewport]
    );

    // Drag to scroll with a mouse - touch/pen already pan natively.
    useEffect(() => {
        const viewport = getViewport();

        let drag = null;

        // Swallows the click a mouse-up would otherwise fire on the card the
        // pointer finished over, so dragging the rail never navigates.
        const blockNextClick = () => {
            const block = (event) => {
                event.preventDefault();
                event.stopPropagation();
            };

            viewport.addEventListener('click', block, {
                capture: true,
                once: true
            });

            window.setTimeout(() => {
                viewport.removeEventListener('click', block, { capture: true });
            }, 0);
        };

        const onPointerDown = (event) => {
            if (event.pointerType !== 'mouse' || event.button !== 0) {
                return;
            }

            drag = {
                x: event.clientX,
                scrollLeft: viewport.scrollLeft,
                isDragging: false
            };
        };

        const onPointerMove = (event) => {
            if (!drag) {
                return;
            }

            const distance = event.clientX - drag.x;

            if (!drag.isDragging) {
                if (Math.abs(distance) < DRAG_THRESHOLD) {
                    return;
                }

                drag.isDragging = true;
                viewport.setPointerCapture(event.pointerId);
                viewport.style.userSelect = 'none';
                viewport.style.cursor = 'grabbing';
                document.getSelection()?.removeAllRanges();
            }

            viewport.scrollLeft = drag.scrollLeft - distance;
            event.preventDefault();
        };

        const onPointerUp = (event) => {
            if (!drag) {
                return;
            }

            const { isDragging } = drag;

            drag = null;
            viewport.style.userSelect = '';
            viewport.style.cursor = '';

            if (viewport.hasPointerCapture?.(event.pointerId)) {
                viewport.releasePointerCapture(event.pointerId);
            }

            if (isDragging) {
                blockNextClick();
            }
        };

        // Cards are links; without this the browser starts a native link drag.
        const onDragStart = (event) => {
            if (drag?.isDragging) {
                event.preventDefault();
            }
        };

        if (viewport) {
            viewport.addEventListener('pointerdown', onPointerDown);
            viewport.addEventListener('pointermove', onPointerMove);
            viewport.addEventListener('pointerup', onPointerUp);
            viewport.addEventListener('pointercancel', onPointerUp);
            viewport.addEventListener('dragstart', onDragStart);
        }

        return () => {
            if (!viewport) {
                return;
            }

            viewport.removeEventListener('pointerdown', onPointerDown);
            viewport.removeEventListener('pointermove', onPointerMove);
            viewport.removeEventListener('pointerup', onPointerUp);
            viewport.removeEventListener('pointercancel', onPointerUp);
            viewport.removeEventListener('dragstart', onDragStart);
            viewport.style.userSelect = '';
            viewport.style.cursor = '';
        };
    }, [getViewport]);

    useEffect(() => {
        const viewport = getViewport();

        // Rail width changes with the window and as cards resolve their content.
        const observer =
            typeof ResizeObserver === 'undefined'
                ? null
                : new ResizeObserver(sync);

        if (viewport) {
            viewport.addEventListener('scroll', sync, { passive: true });
            observer?.observe(viewport);
            // The rail always opens flush left, but arrow enablement still needs
            // a first read - ResizeObserver isn't guaranteed to fire on observe.
            sync();
        }

        return () => {
            viewport?.removeEventListener('scroll', sync);
            observer?.disconnect();
        };
    }, [getViewport, sync]);

    return {
        containerRef,
        viewportRef,
        getViewport,
        canScrollPrev,
        canScrollNext,
        stuckRoundNumber,
        page
    };
};
